<template>
  <div class="task-details" :class="{ 'task-details--modal': isInModalMode }">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        :sticky="!isInModalMode"
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="20">
            {{ $t('common.cancel') }}
          </b-button>
          <OmxPermissionButton
              :allowed="canSave"
              type="is-primary"
              @click="saveClicked"
              icon-left="content-save"
              tabindex="21"
          >{{ $t('common.save') }}</OmxPermissionButton>
        </template>
        <template v-else>
          <b-button v-if="!isInModalMode" @click="backClicked" icon-left="arrow-left">
            {{ $t('common.back') }}
          </b-button>
          <OmxPermissionButton :allowed="canEdit" type="is-primary" @click="editClicked" icon-left="pencil">
            {{ $t('common.edit') }}
          </OmxPermissionButton>
          <OmxPermissionButton :allowed="canDelete" type="is-danger" @click="deleteClicked" icon-left="delete">
            {{ $t('common.delete') }}
          </OmxPermissionButton>
        </template>
      </template>
    </OmxPageHeader>

    <OmxCard class="task-details__card">
      <div class="columns">
        <div class="column is-half">
          <OmxField
              v-if="isEditMode"
              v-model="taskData.title"
              :label="$t('task-details-view.task-title-field')"
              :placeholder="$t('task-details-view.task-title-placeholder')"
              rules="required"
              required
              tabindex="1"
          />
          <OmxField
              v-else
              :label="$t('task-details-view.task-title-field')"
              :value="taskData.title"
              view-only
          />

          <OmxTagInput
              v-model="selectedCategories"
              :label="$t('task-details-view.task-category-field')"
              :data="filteredCategories"
              :loading="isFetchingCategories"
              :view-only="!isEditMode"
              :maxtags="1"
              :allow-new="false"
              :confirm-keys="[]"
              :placeholder="$t('task-details-view.task-category-placeholder')"
              tabindex="2"
              @typing="getFilteredCategories"
              @input="onCategoriesInput"
              @blur="onCategoryInputBlur"
          />

          <OmxField
              v-model="taskData.description"
              type="textarea"
              :label="$t('task-details-view.task-description-field')"
              :placeholder="$t('task-details-view.task-description-placeholder')"
              :view-only="!isEditMode"
              tabindex="4"
          />
        </div>

        <div class="column">
          <div class="columns no-margin" v-if="isEditMode">
            <div class="column">
              <OmxField
                  v-model="taskData.meta.estimation.quantity"
                  type="number"
                  step="any"
                  min="0"
                  :label="$t('task-details-view.task-quantity-placeholder')"
                  :placeholder="$t('task-details-view.task-quantity-placeholder')"
                  tabindex="5"
              />
            </div>
            <div class="column">
              <OmxField
                  v-model="taskData.unit"
                  type="select"
                  :label="$t('task-details-view.task-quantityunit-placeholder')"
                  :placeholder="$t('task-details-view.task-quantityunit-placeholder')"
                  :options="quantityUnitOptions"
                  tabindex="6"
              />
            </div>
          </div>
          <OmxField
              v-else
              :label="$t('task-details-view.task-quantity-placeholder')"
              view-only
          ><template #value>{{ getFormattedQuantity(taskData.meta.estimation, taskData.unit) }}</template></OmxField>

          <div class="columns no-margin">
            <div class="column is-half">
              <OmxField
                  v-model="taskData.meta.estimation.number_of_internal_personnel"
                  type="number"
                  min="0"
                  step="1"
                  :label="$t('task-details-view.task-internal-personnel-number')"
                  :placeholder="$t('task-details-view.task-internal-personnel-number-placeholder')"
                  :view-only="!isEditMode"
                  tabindex="7"
              />
            </div>
            <div class="column is-half">
              <OmxField
                  v-model="taskData.meta.estimation.number_of_external_personnel"
                  type="number"
                  min="0"
                  step="1"
                  :label="$t('task-details-view.task-external-personnel-number')"
                  :placeholder="$t('task-details-view.task-external-personnel-number-placeholder')"
                  :view-only="!isEditMode"
                  tabindex="8"
              />
            </div>
          </div>

          <OmxField
              v-if="isEditMode"
              v-model="taskData.meta.estimation.performance"
              type="number"
              min="0"
              step="any"
              :label="$t('task-details-view.task-performance')"
              :placeholder="$t('task-details-view.task-performance-placeholder')"
              :hint="$t('task-details-view.task-performance-note')"
              tabindex="9"
          />
          <OmxField
              v-else
              :label="$t('task-details-view.task-performance')"
              view-only
          ><template #value>{{ getFormattedPerformance(taskData.meta.estimation.performance, taskData.unit) }}</template></OmxField>
        </div>
      </div>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";
import OmxTagInput from "@/components/OmxTagInput.vue";

export default {
  name: "TaskDetails",
  components: {
    OmxPageHeader,
    OmxCard,
    OmxField,
    OmxTagInput
  },
  props: {
    'project_id': {
      type: String,
    },
    'task_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      taskData: {
        meta: {
          estimation: {
            quantity: null,
            number_of_internal_personnel: null,
            number_of_external_personnel: null,
            performance: null
          }
        },
        unit: ""
      },
      isLoading: false,
      isFetchingCategories: false,
      categoriesData: [],
      filteredCategories: [],
      selectedCategories: []
    }
  },
  methods: {
    readTaskData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getTaskFunction({
        action: "get_task_by_id",
        project_id: this.project_id,
        document_id: this.task_id,
        fill_meta: true
      }).then((result) => {
        this.taskData = result.data["tasks"];
        if (!this.taskData.meta.estimation)
          this.$set(this.taskData.meta, 'estimation', {});
        const category = this.taskData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        this.isLoading = false;
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting tasks:", error?.code);
      });
    },
    createTask: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_task";
      insertTaskFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.task-created'));
        this.$emit('taskAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error creating task:", error?.code);
      });
    },
    updateTask: function (updateData) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_task";
      updateTaskFunction(updateData).then(() => {
        this.isLoading = false;
        this.exitEditMode();
        this.showSuccess(this.$t('messages.task-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error updating task:", error?.code);
      });
    },
    deleteTask: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteTaskFunction({
        action: "delete_task",
        project_id: this.project_id,
        document_id: this.task_id,
      }).then(() => {
        this.isLoading = false;
        this.showSuccess(this.$t('messages.task-deleted'));
        this.$emit('taskDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result:", error?.code);
      });
    },
    readCategoriesData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["tasks"]
      }).then((result) => {
        this.isFetchingCategories = false
        this.categoriesData = result.data["tasks"].category
        this.filteredCategories = this.categoriesData
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories:", error?.code);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      // Edit-as-route: when opened from a routed URL, go to /edit. When
      // opened via the new-task modal, just stay in modal edit mode (the
      // computed already returns true for new mode).
      if (this.isInModalMode) return;
      this.$router.push({
        name: 'TaskEdit',
        params: { project_id: this.project_id, task_id: this.task_id }
      }).catch(() => { /* navigation duplicated */ });
    },
    exitEditMode: function () {
      if (this.isInNewMode || this.isInModalMode) return;
      if (this.$route.name === 'TaskEdit') {
        this.$router.replace({
          name: 'TaskDetails',
          params: { project_id: this.project_id, task_id: this.task_id }
        }).catch(() => { /* navigation duplicated */ });
      }
    },
    async deleteClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('task-details-view.delete-task-confirm-title').toString(),
        message: this.$t('task-details-view.delete-task-confirm-message').toString(),
        confirmText: this.$t('task-details-view.delete-task-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteTask();
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
        return;
      }
      if (this.isEditMode) {
        this.readTaskData();
        this.exitEditMode();
      }
    },
    backClicked: function () {
      this.$router.back();
    },
    async saveClicked() {
      // Required fields must be present before we attempt to save.
      const title = (this.taskData.title || "").toString().trim();
      if (!title) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        project_id: this.project_id,
        title: this.taskData.title,
        description: this.taskData.description,
      }
      let estimation = this.taskData.meta.estimation;
      let hasEstimation = true;

      // Estimation verification
      if (Object.keys(estimation).length !== 0) {
        if (estimation.quantity !== undefined && estimation.quantity !== ""
            && estimation.quantity != null && estimation.quantity > 0) {
          estimation.quantity = parseFloat(estimation.quantity);
          if (this.taskData.unit === undefined || this.taskData.unit === "" || this.taskData.unit == null) {
            this.taskData.unit = this.defaultValues.quantityUnitId;
          }
        } else {
          hasEstimation = false;
        }

        if (estimation.number_of_internal_personnel !== undefined && estimation.number_of_internal_personnel !== ""
            && estimation.number_of_internal_personnel != null && estimation.number_of_internal_personnel > 0) {
          estimation.number_of_internal_personnel = parseInt(estimation.number_of_internal_personnel);
        } else {
          estimation.number_of_internal_personnel = 0;
        }
        if (estimation.number_of_external_personnel !== undefined && estimation.number_of_external_personnel !== ""
            && estimation.number_of_external_personnel != null && estimation.number_of_external_personnel > 0) {
          estimation.number_of_external_personnel = parseInt(estimation.number_of_external_personnel);
        } else {
          estimation.number_of_external_personnel = 0;
        }
        if (estimation.number_of_internal_personnel + estimation.number_of_external_personnel === 0) {
          hasEstimation = false;
        }
        if (estimation.performance !== undefined && estimation.performance !== ""
            && estimation.performance != null && estimation.performance > 0) {
          estimation.performance = parseFloat(estimation.performance);
        } else {
          hasEstimation = false;
        }
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (hasEstimation === true) {
        dataFieldsToUpdate.estimation = estimation;
        dataFieldsToUpdate.unit = this.taskData.unit
        if (this.isInNewMode) {
          this.createTask(dataFieldsToUpdate);
        } else {
          dataFieldsToUpdate.document_id = this.task_id;
          this.updateTask(dataFieldsToUpdate);
        }
      } else {
        const ok = await this.$omxConfirm({
          title: this.$t('task-details-view.modify-estimation-confirm-title').toString(),
          message: this.$t('task-details-view.modify-estimation-confirm-message').toString(),
          confirmText: this.$t('task-details-view.modify-estimation-confirm-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-warning',
          icon: 'alert-circle'
        });
        if (!ok) return;
        if (this.isInNewMode) {
          this.createTask(dataFieldsToUpdate);
        } else {
          dataFieldsToUpdate.document_id = this.task_id;
          this.updateTask(dataFieldsToUpdate);
          this.readTaskData();
        }
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoriesData
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoriesData.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.newTag = '';
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("TaskDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoriesData()
    if (this.isInNewMode) {
      console.log("creating new task");
    } else {
      this.readTaskData();
    }
  },
  computed: {
    isInNewMode() {
      return this.task_id === "new";
    },
    isInModalMode() {
      // When opened from Tasks.vue's <b-modal>, the router route is the
      // surrounding ProjectDetails — not TaskDetails/TaskEdit.
      return this.$route.name !== 'TaskDetails' && this.$route.name !== 'TaskEdit';
    },
    isEditMode() {
      // Edit-as-route on TaskDetails. Modal usage falls back to the legacy
      // single-screen edit (always editable in modal context).
      return this.$route.name === 'TaskEdit' || this.isInNewMode || this.isInModalMode;
    },
    headerTitle() {
      if (this.isInNewMode) return this.$t('task-details-view.new-task-title');
      return this.getAdjustedTitle(this.taskData.title);
    },
    headerSubtitle() {
      if (this.selectedCategories && this.selectedCategories[0]) {
        return this.selectedCategories[0];
      }
      return null;
    },
    headerPills() {
      const pills = [];
      if (this.isTemplateMode) {
        pills.push({ label: this.$t('project-details-view.view-template-title'), tone: 'draft', dot: false });
      }
      return pills;
    },
    canEdit() {
      return !this.isEditMode
          && ((!this.is_template && this.currentUserAccess('TaskDetails', 'UPDATE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE')));
    },
    canSave() {
      return (!this.is_template && this.currentUserAccess('TaskDetails', 'UPDATE', this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canDelete() {
      return !this.isEditMode && !this.isInNewMode
          && ((!this.is_template && this.currentUserAccess('TaskDetails', 'DELETE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'DELETE')));
    },
    quantityUnitOptions() {
      return (this.quantityUnits || []).map(opt => ({
        value: opt.id,
        label: this.$t(opt.translationKey)
      }));
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('task-details-view.new-task-title')
      } else if (this.isEditMode) {
        return this.$t('task-details-view.edit-task-title') + " " + this.getAdjustedTitle(this.taskData.title)
      } else {
        return this.$t('task-details-view.view-task-title') + " " + this.getAdjustedTitle(this.taskData.title)
      }
    },
    isTemplateMode() {
      return this.is_template
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.task-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.task-details--modal {
  // When opened in a b-modal, drop outer padding so it fills the modal cleanly.
  padding: 4px;
}
.task-details__card {
  ::v-deep .columns:last-child { margin-bottom: 0; }
}
.estimation-value {
  max-width: 50%;
}
.no-margin {
  margin-bottom: 0px !important;
}
</style>